import {
  ODSButton,
  ODSCardContentBasic,
  ODSCardImage,
  ODSColumn,
  ODSDivider,
  ODSGrid,
  ODSText,
} from '@telekom-ods/react-ui-kit';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import compute from '../../assets/images/compute.svg';
import tdiLogoBlack from '../../assets/images/tdi_connect_logo_black.svg';
import tdiLogoWhite from '../../assets/images/tdi_connect_logo_white.svg';
import Tooltip from '../../components/Tooltip';
import { handleAuthNavigation } from '../../helpers/handleAuthNavigation';
import useAppContext from '../../hooks/useAppContext';

import caasDarkTheme from './caas_dark_theme.png';
import caasWhiteTheme from './caas_white_theme.png';
import HomeStyles from './Home.module.css';

import useAuth from '@/components/AppLayout/hooks/useAuth';

interface Product {
  title: string;
  subheader: string;
  image: string;
  description: string;
  buttonText: string;
  onClick: () => void;
  href?: string;
}

const Home: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { authenticated, interactionInProgress, login } = useAuth();
  const { cookiesAccepted, theme } = useAppContext() as {
    cookiesAccepted: boolean;
    theme: 'dark' | 'light';
  };

  const products: Product[] = [
    {
      title: intl.formatMessage({ id: 'computeTitle' }),
      subheader: intl.formatMessage({ id: 'computeSubheader' }),
      image: compute,
      description: intl.formatMessage({ id: 'computeDescription' }),
      onClick: () => {
        navigate('products/compute/specification');
      },
      buttonText: intl.formatMessage({ id: 'computeButtonText' }),
    },
    {
      title: intl.formatMessage({ id: 'caasTitle' }),
      subheader: intl.formatMessage({ id: 'computeSubheader' }),
      image: theme === 'dark' ? caasDarkTheme : caasWhiteTheme,
      description: intl.formatMessage({ id: 'caasDescription' }),
      buttonText: intl.formatMessage({ id: 'caasButtonText' }),
      onClick: () => {
        navigate('products/caas/specification');
      },
    },
  ];

  return (
    <ODSGrid
      container
      columns={14}
    >
      <ODSGrid columns={14}>
        <>
          <ODSColumn
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ODSColumn className={`app-content-info-logo ${HomeStyles.logoContainer ?? ''}`}>
              <img
                className={HomeStyles.tdiLogo}
                src={theme === 'dark' ? tdiLogoWhite : tdiLogoBlack}
                alt='tdi'
              />
              <ODSText
                as='h1'
                className={HomeStyles.headingText}
              >
                {intl.formatMessage({ id: 'iaasDescription' })}
              </ODSText>
              {!authenticated && (
                <Tooltip
                  key='home-login'
                  title={
                    !cookiesAccepted ? intl.formatMessage({ id: 'privacyPolicyConfirmation' }) : ''
                  }
                >
                  <ODSButton
                    className='rounded-button'
                    buttonType='standard'
                    disabled={!cookiesAccepted || interactionInProgress}
                    label={intl.formatMessage({ id: 'login' })}
                    size='large'
                    variant='primary'
                    onClick={() => {
                      handleAuthNavigation({ authenticated, cookiesAccepted, login, navigate });
                    }}
                  />
                </Tooltip>
              )}
            </ODSColumn>
            {!cookiesAccepted && (
              <ODSText as='p'>{intl.formatMessage({ id: 'privacyPolicyConfirmation' })}</ODSText>
            )}
          </ODSColumn>
          <ODSColumn>
            <ODSDivider
              className={HomeStyles.divider}
              variant='horizontal'
            />
            <ODSText
              as='h2'
              className={HomeStyles.marketplace}
            >
              {intl.formatMessage({ id: 'marketPlaceText' })}
            </ODSText>
            <ODSColumn className={HomeStyles['service-cards']}>
              {products.map(product => (
                <ODSColumn
                  className={HomeStyles['grid-container']}
                  key={product.title}
                  style={{ display: 'flex', justifyContent: 'center', padding: 0 }}
                >
                  <ODSCardImage
                    className={HomeStyles.card ?? ''}
                    actionSlot={
                      <ODSButton
                        className='rounded-button'
                        label={product.buttonText}
                        onClick={product.onClick}
                        onKeyDown={product.onClick}
                      />
                    }
                    backgroundImageProps={{
                      alt: product.subheader,
                      src: product.image,
                      style: { objectFit: 'contain', padding: '1rem' },
                    }}
                    contentSlot={
                      <div className={HomeStyles.ODSCardImage}>
                        <ODSCardContentBasic
                          className='ods-card-content-basic-container card-content'
                          content={product.description}
                          heading={product.title}
                          label={product.subheader}
                          showContent
                          size='large'
                          titleHeading='h4'
                          style={{
                            backgroundColor: 'var(--colours-basic-background-card)',
                          }}
                        />
                      </div>
                    }
                    href={product.href ?? ''}
                    imagePosition='top'
                    size='medium'
                    target='_blank'
                  />
                </ODSColumn>
              ))}
            </ODSColumn>
          </ODSColumn>
        </>
      </ODSGrid>
    </ODSGrid>
  );
};

export default Home;
